import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {Auth} from '../actions/auth.actions';
import {Account, AccountType} from '../models/accounts/account.model';
import {Accounts} from '../actions/accounts.actions';
import {AccountsService} from '../services/accounts.service';
import {tap} from 'rxjs/operators';
import {Job} from '../models/pos/job.model';
import ClearState = Auth.ClearState;
import GetAccount = Accounts.GetAccount;
import GetAccounts = Accounts.GetAccounts;
import CreateAccount = Accounts.CreateAccount;
import UpdateAccount = Accounts.UpdateAccount;
import DeleteAccount = Accounts.DeleteAccount;
import GetAccountOrders = Accounts.GetAccountOrders;

export interface AccountsStateModel {
  debtors: Array<Account>;
  creditors: Array<Account>;
  currentDebtor: Account;
  currentCreditor: Account;
  accountOrders: Array<Job>;
}

@State<AccountsStateModel>({
  name: 'accounts',
  defaults: {
    debtors: [],
    creditors: [],
    currentDebtor: null,
    currentCreditor: null,
    accountOrders: [],
  }
})
@Injectable()
export class AccountsState {
  constructor(private accountService: AccountsService) {
  }

  @Selector()
  static accountOrders(state: AccountsStateModel): Array<Job> {
    return state.accountOrders;
  }

  @Selector()
  static debtors(state: AccountsStateModel): Array<Account> {
    return state.debtors;
  }

  @Selector()
  static creditors(state: AccountsStateModel): Array<Account> {
    return state.creditors;
  }

  @Selector()
  static debtor(state: AccountsStateModel): Account {
    return state.currentDebtor;
  }

  @Selector()
  static creditor(state: AccountsStateModel): Account {
    return state.currentCreditor;
  }

  @Selector()
  static account(state: AccountsStateModel): (type: AccountType) => Account {
    return (type: AccountType) => {
      switch (type) {
        case 'DEBTOR':
          return state.currentDebtor;
        case 'CREDITOR':
          return state.currentCreditor;
      }
    };
  }

  @Selector()
  static accounts(state: AccountsStateModel): (type: AccountType) => Account[] {
    return (type: AccountType) => {
      switch (type) {
        case 'DEBTOR':
          return state.debtors;
        case 'CREDITOR':
          return state.creditors;
      }
    };
  }

  @Action(GetAccounts)
  getAccounts(ctx: StateContext<AccountsStateModel>, action: GetAccounts): Observable<Account[]> {
    if (!action.force) {
      if (action.type === 'DEBTOR' && ctx.getState().debtors.length > 0) {
        return of(ctx.getState().debtors);
      } else if (action.type === 'CREDITOR' && ctx.getState().creditors.length > 0) {
        return of(ctx.getState().creditors);
      }
    }

    return this.accountService.getAccounts(action.type).pipe(
      tap(accounts => {
        if (action.type === 'DEBTOR') {
          ctx.patchState({
            debtors: accounts
          });
        } else if (action.type === 'CREDITOR') {
          ctx.patchState({
            creditors: accounts
          });
        }
      })
    );
  }

  @Action(GetAccountOrders)
  getAccountOrders(ctx: StateContext<AccountsStateModel>, action: GetAccountOrders): Observable<Job[]> {
    if (!action.force) {
      const accountOrders = ctx.getState().accountOrders;
      if (accountOrders.length > 0 && accountOrders.every(order => order.debtorId === action.id)) {
        return of(accountOrders);
      }
    }

    return this.accountService.getAccountOrders(action.id).pipe(
      tap(orders => {
        ctx.patchState({
          accountOrders: orders
        });
      })
    );
  }

  @Action(GetAccount)
  getAccount(ctx: StateContext<AccountsStateModel>, action: GetAccount): Observable<Account> {
    if (!action.force) {
      const currentDebtor = ctx.getState().currentDebtor;
      const currentCreditor = ctx.getState().currentDebtor;

      if (currentDebtor && currentDebtor.id === action.id) {
        return of(currentDebtor);
      }

      if (currentCreditor && currentCreditor.id === action.id) {
        return of(currentCreditor);
      }

      const cachedDebtor = ctx.getState().debtors.find(debtor => debtor.id === action.id);
      if (cachedDebtor) {
        ctx.patchState({
          currentDebtor: cachedDebtor
        });
        return of(cachedDebtor);
      }
      const cachedCreditor = ctx.getState().creditors.find(creditor => creditor.id === action.id);
      if (cachedCreditor) {
        ctx.patchState({
          currentCreditor: cachedCreditor
        });
        return of(cachedCreditor);
      }
    }

    return this.accountService.getAccount(action.id).pipe(
      tap(account => {
        if (account.type === 'DEBTOR') {
          ctx.patchState({
            currentDebtor: account
          });
        } else if (account.type === 'CREDITOR') {
          ctx.patchState({
            currentCreditor: account
          });
        }
      })
    );
  }

  @Action(CreateAccount)
  createAccount(ctx: StateContext<AccountsStateModel>, action: CreateAccount): Observable<Account> {
    return this.accountService.createAccount(action.account).pipe(
      tap(account => {
        if (account.type === 'DEBTOR') {
          ctx.patchState({
            currentDebtor: account
          });
        } else if (account.type === 'CREDITOR') {
          ctx.patchState({
            currentCreditor: account
          });
        }
      })
    );
  }

  @Action(UpdateAccount)
  updateAccount(ctx: StateContext<AccountsStateModel>, action: UpdateAccount): Observable<Account> {
    return this.accountService.updateAccount(action.account).pipe(
      tap(account => {
        if (account.type === 'DEBTOR') {
          ctx.patchState({
            currentDebtor: account
          });
        } else if (account.type === 'CREDITOR') {
          ctx.patchState({
            currentCreditor: account
          });
        }
      })
    );
  }

  @Action(DeleteAccount)
  deleteAccount(ctx: StateContext<AccountsStateModel>, action: DeleteAccount): Observable<Account> {
    const accounts = action.account.type === 'DEBTOR' ? ctx.getState().debtors : ctx.getState().creditors;
    const index = accounts.indexOf(action.account);
    return this.accountService.deleteAccount(action.account).pipe(
      tap(account => {
        if (account.type === 'DEBTOR') {
          ctx.patchState({
            currentDebtor: null,
            debtors: [
              ...accounts.slice(0, index),
              ...index === accounts.length - 1 ? [] : accounts.slice(index + 1)
            ]
          });
        } else if (account.type === 'CREDITOR') {
          ctx.patchState({
            currentCreditor: null,
            creditors: [
              ...accounts.slice(0, index),
              ...index === accounts.length - 1 ? [] : accounts.slice(index + 1)
            ]
          });
        }
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<AccountsStateModel>): void {
    ctx.setState({
      debtors: [],
      creditors: [],
      currentCreditor: null,
      currentDebtor: null,
      accountOrders: [],
    });
  }
}
